/**
 * Email template configuration — stable lookup keys and built-in fallbacks.
 *
 * WHY THIS FILE EXISTS (dashboard#863)
 *
 * `CompanyInviteFormComponent` used to load its email body from a hardcoded
 * Firestore document id (`Rm1CzWDUJeN9SiJTdWkN`). That id existed in the dev
 * Firebase project and not in `scan-ninja-production`, so every production
 * company invite failed — silently. An environment-specific Firestore key
 * compiled into a component is unfixable by configuration: nothing makes the
 * projects agree and nothing fails at build time when they diverge.
 *
 * Templates are now looked up by a stable `name` field, which is identical in
 * every Firebase project and every Angular build configuration, and a built-in
 * default is shipped with the bundle so a missing document degrades the email
 * body rather than removing the feature.
 */

/**
 * Stable `name` of the company-invite document in the `email-templates`
 * Firestore collection. Seeded by `scripts/seed-email-templates.ts`; identical
 * across dev, qa and production.
 */
export const COMPANY_INVITE_TEMPLATE_NAME = 'Company Invite';

/**
 * Every placeholder `CompanyInviteFormComponent.getEmailContent()` substitutes.
 *
 * A template that omits one of these sends a literal `{{TOKEN}}` to a customer,
 * so both the built-in default and the seed script assert against this list.
 */
export const COMPANY_INVITE_PLACEHOLDERS: readonly string[] = [
  '{{INVITE_CODE}}',
  '{{SENDER}}',
  '{{CUSTOM_MESSAGE}}',
  '{{ORGANIZATION}}',
  '{{WEB_URL}}',
];

/**
 * Built-in company-invite email body, used when the Firestore document is
 * missing or unreadable.
 *
 * Kept byte-identical to the content written by `scripts/seed-email-templates.ts`
 * so the fallback and the seeded document cannot drift apart.
 *
 * Table layout and inline styles are deliberate and are NOT a violation of the
 * app's "no inline styles" rule: this string is never rendered by Angular, it
 * is posted to the email service. Mail clients strip stylesheets and most do
 * not support flex or grid, so inline styles on nested tables are the only
 * layout that survives delivery.
 */
export const COMPANY_INVITE_FALLBACK_TEMPLATE = `<table role="presentation" width="100%" cellpadding="0" cellspacing="0" style="background:#f4f6f8;padding:24px 0;font-family:Helvetica,Arial,sans-serif;">
<tr><td align="center">
<table role="presentation" width="600" cellpadding="0" cellspacing="0" style="background:#ffffff;border-radius:6px;overflow:hidden;max-width:600px;">
<tr><td style="height:4px;background:#1f6fb8;background-image:linear-gradient(90deg,#1f6fb8 0%,#7b3fa0 50%,#c0397e 100%);font-size:0;line-height:0;">&nbsp;</td></tr>
<tr><td style="padding:28px 32px 8px 32px;">
<h1 style="margin:0 0 14px 0;font-size:20px;line-height:1.3;color:#1b2a3a;">You have been invited to join {{ORGANIZATION}}</h1>
<p style="margin:0 0 16px 0;font-size:14px;line-height:1.55;color:#33475b;">{{SENDER}} has invited you to join <strong>{{ORGANIZATION}}</strong> on the Scan Ninja platform.</p>
<p style="margin:0 0 20px 0;font-size:14px;line-height:1.55;color:#33475b;">{{CUSTOM_MESSAGE}}</p>
<table role="presentation" cellpadding="0" cellspacing="0" width="100%" style="margin:0 0 22px 0;">
<tr><td style="background:#f0f4f8;border-left:3px solid #1f6fb8;padding:14px 16px;">
<p style="margin:0 0 6px 0;font-size:11px;letter-spacing:.06em;text-transform:uppercase;color:#5b6b7b;">Your invite code</p>
<p style="margin:0;font-size:20px;font-weight:bold;letter-spacing:.08em;color:#1b2a3a;font-family:Menlo,Consolas,monospace;">{{INVITE_CODE}}</p>
<p style="margin:8px 0 0 0;font-size:12px;color:#5b6b7b;">This code expires in 7 days.</p>
</td></tr></table>
<table role="presentation" cellpadding="0" cellspacing="0" style="margin:0 0 24px 0;"><tr>
<td style="background:#1f6fb8;border-radius:4px;"><a href="{{WEB_URL}}" style="display:inline-block;padding:11px 22px;font-size:14px;font-weight:bold;color:#ffffff;text-decoration:none;">Accept the invitation</a></td>
</tr></table>
<p style="margin:0 0 6px 0;font-size:12px;line-height:1.5;color:#5b6b7b;">If the button does not work, open <a href="{{WEB_URL}}" style="color:#1f6fb8;">{{WEB_URL}}</a> and enter the code above.</p>
<p style="margin:0 0 20px 0;font-size:12px;line-height:1.5;color:#5b6b7b;">If you were not expecting this invitation, you can ignore this email.</p>
</td></tr>
<tr><td style="border-top:1px solid #e3e8ee;padding:16px 32px 22px 32px;">
<p style="margin:0;font-size:11px;line-height:1.5;color:#8294a5;">Scan Ninja Inc. &middot; 4201 Main St, Suite 241, Houston, TX 77002 &middot; scanninja.ai</p>
</td></tr>
</table></td></tr></table>`;
